import React, { useMemo, useState } from "react";
import { exportToCsv } from "./reportUtils";
import "./reports.css";

export default function ReportTablePage({
  title,
  subtitle,
  filters,
  rows,
  columns,
  loading,
  onSearch,
  onClear,
  exportFileName = "report.csv",
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const totalPages = Math.max(1, Math.ceil((rows?.length || 0) / pageSize));

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (rows || []).slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const exportRows = useMemo(() => {
    return (rows || []).map((row, index) => {
      const item = { RowNo: index + 1 };
      columns.forEach((col) => {
        item[col.label] =
          typeof col.exportValue === "function"
            ? col.exportValue(row)
            : row[col.key] ?? "";
      });
      return item;
    });
  }, [rows, columns]);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  return (
    <div className="page-container">
      <div className="reports-header">
        <div>
          <h1>{title}</h1>
          <p className="subtitle">{subtitle}</p>
        </div>

        <div className="reports-header-actions">
          <button className="btn report-secondary-btn" onClick={onSearch}>
            Refresh
          </button>
          <button
            className="btn primary"
            onClick={() => exportToCsv(exportFileName, exportRows)}
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="card report-filter-card">
        <div className="report-filter-grid">{filters}</div>

        <div className="report-filter-actions">
          <button className="btn primary" onClick={onSearch}>
            Search
          </button>
          <button className="btn report-secondary-btn" onClick={onClear}>
            Clear
          </button>
        </div>
      </div>

      <div className="card">
        <div className="report-table-toolbar">
          <div className="report-total">
            Total Records: <strong>{rows?.length || 0}</strong>
          </div>

          <div className="report-toolbar-right">
            <span>Rows per page</span>
            <select
              className="input page-size-input"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="report-loading">Loading report...</div>
        ) : (
          <div className="report-table-wrap">
            <table className="table report-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  {columns.map((col) => (
                    <th key={col.label}>{col.label}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="empty">
                      No records found
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row, idx) => (
                    <tr key={row.transaction_id || row.member_id || idx}>
                      <td>{(page - 1) * pageSize + idx + 1}</td>
                      {columns.map((col) => (
                        <td key={col.label}>
                          {typeof col.render === "function"
                            ? col.render(row)
                            : row[col.key]}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="report-pagination">
          <button
            className="btn report-secondary-btn"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>

          <span>
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </span>

          <button
            className="btn report-secondary-btn"
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}